import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, Briefcase, Building2, Globe2, TrendingUp, Facebook, Instagram, Bell } from 'lucide-react';
import FeaturedJobsCarousel from '@/components/FeaturedJobsCarousel';
import NewsletterForm from '@/components/NewsletterForm';
import Footer from '@/components/Footer';
import { API } from '../App';

const HomePage = ({ user }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/my-messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-1.5 sm:space-x-2">
              <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
              <span className="text-lg sm:text-2xl font-bold text-slate-900">
                JobSuiza
              </span>
            </Link>

            {/* Navigation Items - Always visible */}
            <div className="flex items-center space-x-1.5 sm:space-x-4">
              {/* Blog Link */}
              <Link to="/blog">
                <Button variant="ghost" size="sm" className="text-slate-700 hover:text-red-600 px-2 sm:px-3 text-xs sm:text-sm">
                  Blog
                </Button>
              </Link>
              
              {/* Ofertas Button */}
              <Link to="/ofertas">
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm px-2.5 sm:px-4 h-8 sm:h-9">
                  Ofertas
                </Button>
              </Link>
              
              <Link to="/anuncios">
                <Button size="sm" variant="outline" className="text-xs sm:text-sm px-2.5 sm:px-4 h-8 sm:h-9">
                  Tablón
                </Button>
              </Link>
              
              {/* Social Media Icons */}
              <div className="hidden sm:flex items-center space-x-1.5 sm:space-x-2">
                <a 
                  href="https://www.facebook.com/jobsuizaoficial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </a>
                <a 
                  href="https://www.instagram.com/jobsuizaoficial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white rounded-full transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </a>
              </div>
              
              {/* Login/Dashboard - Hidden on mobile */}
              <div className="hidden sm:flex items-center gap-2">
                {user ? (
                  <>
                    <Link to="/mis-anuncios" className="relative">
                      <Button variant="ghost" size="sm" className="p-2">
                        <Bell className="w-5 h-5 text-slate-600" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </Button>
                    </Link>
                    <Link to="/dashboard">
                      <Button variant="outline" size="sm">
                        Dashboard
                      </Button>
                    </Link>
                  </>
                ) : (
                  <Link to="/auth">
                    <Button variant="outline" size="sm">
                      Entrar
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 sm:pt-28 pb-12 sm:pb-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-emerald-50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full mb-4 sm:mb-8">
            <Globe2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
            <span className="text-xs sm:text-sm font-medium text-emerald-800">Tu Puerta al Mercado Laboral Suizo</span>
          </div>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black mb-3 sm:mb-6 leading-tight text-slate-900">
            Encuentra Trabajo en{' '}
            <span className="text-red-600">Suiza</span>
          </h1>
          <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-slate-700 mb-3 sm:mb-6">
            Donde se Valora el Español
          </p>
          
          <p className="text-sm sm:text-lg text-slate-600 mb-6 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
            La primera plataforma dedicada a oportunidades laborales en Suiza que requieren español.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4">
            <Link to="/ofertas" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-10 text-base sm:text-lg bg-red-600 hover:bg-red-700 text-white shadow-lg">
                <Search className="w-5 h-5 mr-2" />
                Ver Ofertas
              </Button>
            </Link>
            <Link to="/blog" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-10 text-base sm:text-lg border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white">
                Leer Blog
              </Button>
            </Link>
          </div>
          
          {/* Mobile login link */}
          <div className="sm:hidden mt-4">
            <Link to={user ? "/dashboard" : "/auth"} className="text-red-600 font-medium text-sm">
              {user ? "Ir a Mi Dashboard →" : "Iniciar Sesión →"}
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Jobs Carousel */}
      <section className="py-10 sm:py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4 text-slate-900">Ofertas Destacadas</h2>
            <p className="text-sm sm:text-xl text-slate-600">Descubre las mejores oportunidades en Suiza</p>
          </div>
          <FeaturedJobsCarousel 
            onJobClick={(jobId) => window.location.href = `/ofertas/${jobId}`}
          />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4 text-slate-900">¿Por Qué JobSuiza?</h2>
            <p className="text-sm sm:text-xl text-slate-600">Tu ventaja competitiva en el mercado laboral suizo</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8">
            <div className="p-5 sm:p-8 bg-slate-50 rounded-2xl text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-6 bg-red-100 rounded-2xl flex items-center justify-center">
                <Search className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
              </div>
              <h3 className="text-base sm:text-xl font-bold mb-2 text-slate-900">Ofertas Exclusivas</h3>
              <p className="text-sm text-slate-600">
                Trabajos que buscan hispanohablantes en Suiza
              </p>
            </div>

            <div className="p-5 sm:p-8 bg-slate-50 rounded-2xl text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-6 bg-emerald-100 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
              </div>
              <h3 className="text-base sm:text-xl font-bold mb-2 text-slate-900">Guías y Consejos</h3>
              <p className="text-sm text-slate-600">
                Blog con tips para mudarte y trabajar en Suiza
              </p>
            </div>

            <div className="p-5 sm:p-8 bg-slate-50 rounded-2xl text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-6 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
              </div>
              <h3 className="text-base sm:text-xl font-bold mb-2 text-slate-900">Empresas Verificadas</h3>
              <p className="text-sm text-slate-600">
                Ofertas de empresas reales que operan en Suiza
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 sm:py-16 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <NewsletterForm />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-r from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-xl sm:text-4xl font-bold mb-3 sm:mb-6">¿Listo para tu Aventura Suiza?</h2>
          <p className="text-sm sm:text-xl mb-5 sm:mb-8 opacity-90">
            Miles de oportunidades te esperan
          </p>
          <Link to="/ofertas">
            <Button size="lg" className="h-11 sm:h-14 px-6 sm:px-10 text-sm sm:text-lg bg-white text-red-600 hover:bg-slate-100">
              Explorar Ofertas Ahora
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
